import { Track } from "@/src/lib/tracking";
/**
 * 兑换码与奖励系统集成Hook
 * 处理兑换成功后的奖励弹窗显示和系统集成
 */

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RewardPopupCreateAble } from "../components/modal/RewardPopup/RewardPopup";
import { QUERY_KEYS } from "@config/constant";
import { Reward } from "./useQueryRewards";

export interface RedeemCodeIntegration {
  triggerRewardPopup: (rewards: Reward[]) => Promise<void>;
  createMockRewardRecords: (
    rewards: Reward[],
    ownerId: string,
  ) => Promise<void>;
  refreshRewards: () => Promise<void>;
}

export const useRedeemCodeIntegration = (): RedeemCodeIntegration => {
  const queryClient = useQueryClient();

  const triggerRewardPopup = useCallback(
    async (rewards: Reward[]): Promise<void> => {
      try {
        Track.log("info", "RedeemCodeIntegration", "Triggering reward popup", {
          rewards: String(rewards),
        });

        await RewardPopupCreateAble({
          rewards,
          modalProps: {},
        });

        Track.log(
          "info",
          "RedeemCodeIntegration",
          "Reward popup displayed successfully",
        );
      } catch (error) {
        Track.error(error, {
          tag: "RedeemCodeIntegration",
          msg: "Failed to show reward popup:",
        });
        throw error;
      }
    },
    [],
  );

  const createMockRewardRecords = useCallback(
    async (rewards: Reward[], ownerId: string): Promise<void> => {
      try {
        Track.log(
          "info",
          "RedeemCodeIntegration",
          "Creating mock reward records",
          {
            rewards: String(rewards),
            ownerId,
          },
        );

        const mockRecords = rewards.map((reward) => ({
          orderId: reward.orderId,
          rewardType: reward.type,
          metadata: `${reward.name}-${reward.value}`,
          ownerId,
          createdAt: new Date().toISOString(),
        }));

        Track.log(
          "info",
          "RedeemCodeIntegration",
          "Mock reward records created",
          {
            mockRecords: String(mockRecords),
          },
        );

        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        Track.error(error, {
          tag: "RedeemCodeIntegration",
          msg: "Failed to create reward records:",
        });
        throw error;
      }
    },
    [],
  );

  const refreshRewards = useCallback(async (): Promise<void> => {
    try {
      Track.log("info", "RedeemCodeIntegration", "Refreshing rewards query");

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_FEATURE_EQUITYLIST],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CREDENTIAL_CUSTOM_ME],
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_FEATURE],
      });

      Track.log(
        "info",
        "RedeemCodeIntegration",
        "Rewards query refreshed successfully",
      );
    } catch (error) {
      Track.error(error, {
        tag: "RedeemCodeIntegration",
        msg: "Failed to refresh rewards query:",
      });
      throw error;
    }
  }, [queryClient]);

  return {
    triggerRewardPopup,
    createMockRewardRecords,
    refreshRewards,
  };
};
